import { call, put, select, take, takeLatest } from 'redux-saga/effects';
import axios from 'src/api/Api';
import { errorHandler } from 'src/errorHandler/errorHandler';
import { logUiErrorToBackend } from 'src/utils/logUiErrorToBackend';
import {
    FETCH_FILTERS_REQUEST,
    FETCH_FILTERS_SUCCESS,
    FETCH_FILTERS_FAILURE,
    SAVE_FILTERS_REQUEST,
    SAVE_FILTERS_SUCCESS,
    SAVE_FILTERS_FAILURE,
    GET_DASHBOARD_LIST,
    GET_DASHBOARD_LIST_SUCCESS,
} from '../actions';

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Maps the current dashboardFilters slice of state into the PUT payload
 * expected by /dashboard/dashboardPreference.
 *
 * Each filter value may be a single object or an array of objects.
 * Multiple selected values are joined as a comma-separated string.
 */
const toCommaSeparated = (value, key) => {
    if (Array.isArray(value)) {
        return value.map((item) => item[key]).filter(Boolean).join(',');
    }
    return value?.[key] ?? '';
};

export const mapFilterStateToPayload = (filtersState) => ({
    districtList: toCommaSeparated(filtersState.district, 'districtCode'),
    statusList:   toCommaSeparated(filtersState.status, 'filterCode'),
    allocated:    toCommaSeparated(filtersState.allocated, 'filterCode'),
    surgeryType:  toCommaSeparated(filtersState.surgeryType, 'filterCode'),
});

// ---------------------------------------------------------------------------
// fetchFiltersSaga
// ---------------------------------------------------------------------------

export function* fetchFiltersSaga() {
    const baseSiteId = localStorage.getItem('baseSiteId') || 'defaultSite';
    const prefsUrl = `/occ/v2/${baseSiteId}/dashboard/getDashboardPreference`;

    try {
        const { data: prefs } = yield call(axios.get, prefsUrl);

        // If getDashboardList hasn't completed yet, wait for it
        // let districtFilterList = yield select((state) => state.dashboardListReducer.districtFilterList ?? []);
        // if (districtFilterList.length === 0) {
        //     yield take(GET_DASHBOARD_LIST_SUCCESS);
        //     districtFilterList = yield select((state) => state.dashboardListReducer.districtFilterList ?? []);
        // }

        const options = {
            districtFilterList: prefs?.districtFilterList,
            statusList: prefs?.setStatusList ?? [],
            allocatedList: prefs?.allocatedFilter ?? [],
            surgeryTypeList: prefs?.surgeryTypeFilter ?? [],
            defaultDate: prefs?.defaultDate ?? '',
        };

        yield put({ type: FETCH_FILTERS_SUCCESS, payload: { options } });

        // Get existing applied filters from state
        const appliedFilters = yield select((state) => state.dashboardFiltersReducer?.appliedFilters);
        
        // Use applied filters if they exist, otherwise fall back to defaults from API response
        const districtFilter = appliedFilters?.districtFilter?.length > 0
            ? appliedFilters.districtFilter
            : options.districtFilterList?.filter(d => d.isDefault).map(d => d.districtCode) || [];
        const statusFilter = appliedFilters?.statusFilter?.length > 0
            ? appliedFilters.statusFilter
            : options.statusList?.filter(s => s.isDefault).map(s => s.filterCode) || [];
        const allocatedFilter = appliedFilters?.allocatedFilter?.length > 0
            ? appliedFilters.allocatedFilter
            : options.allocatedList?.filter(a => a.isDefault).map(a => a.filterCode) || [];
        const surgeryTypeFilter = appliedFilters?.surgeryTypeFilter?.length > 0
            ? appliedFilters.surgeryTypeFilter
            : options.surgeryTypeList?.filter(s => s.isDefault).map(s => s.filterCode) || [];

        yield put({ 
            type: GET_DASHBOARD_LIST, 
            payload: { 
                districtFilter, 
                statusFilter, 
                allocatedFilter, 
                surgeryTypeFilter 
            } 
        });
    } catch (error) {
        logUiErrorToBackend(error, { apiEndPoint: prefsUrl });
        yield put({
            type: FETCH_FILTERS_FAILURE,
            payload: errorHandler(error?.response?.status || '503'),
        });
    }
}

// ---------------------------------------------------------------------------
// saveFiltersSaga
// ---------------------------------------------------------------------------

export function* saveFiltersSaga(action) {
    const baseSiteId = localStorage.getItem('baseSiteId') || 'defaultSite';
    const url = `/occ/v2/${baseSiteId}/dashboard/dashboardPreference`;

    try {
        const payload = action.payload;

        yield call(axios.put, url, payload);

        yield put({ type: SAVE_FILTERS_SUCCESS });

        // Re-fetch the dashboard list to reflect the saved filters
       // yield put({ type: GET_DASHBOARD_LIST, payload: action.payload });

        // // Wait for dashboard list to complete before fetching filters
        // // so we get the updated isDefault values
        // yield take(GET_DASHBOARD_LIST_SUCCESS);

        // Re-fetch filters to get updated isDefault values
        yield put({ type: FETCH_FILTERS_REQUEST });

    } catch (error) {
        logUiErrorToBackend(error, { apiEndPoint: url });
        yield put({
            type: SAVE_FILTERS_FAILURE,
            payload: errorHandler(error?.response?.status || '503'),
        });
    }
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

export function* watchFetchFilters() {
    yield takeLatest(FETCH_FILTERS_REQUEST, fetchFiltersSaga);
}

export function* watchSaveFilters() {
    yield takeLatest(SAVE_FILTERS_REQUEST, saveFiltersSaga);
}
